/**
 * @ngdoc service
 * @name flowListManager
 * @module flowingly.runner.services
 *
 * @description This service is responsible managing the various lists,
 *              it will fetch / persist data using the apiService,
 *              and format the list data using appropriate services.
 */

import angular from 'angular';
import { SharedAngular } from '@Client/@types/sharedAngular';
import IReportFlowModelDetail from '@Shared.Angular/@types/core/contracts/queryModel/reports/reportFlowModelDetail';
import { Guid } from '@Shared.Angular/@types/guid';
import { IGroupedFlowsForUserPascalCase } from '@Shared.Angular/@types/core/contracts/queryModel/flows/groupedFlowsForUser';
import { IFormattedCategory } from './start.flows.formatter';
import { IExpandedCategoryInfo } from '@Client/runner.nav.categories/components/CategoryNavigation';

angular
  .module('flowingly.runner.services')
  .factory('flowListManager', flowListManager);

flowListManager.$inject = [
  'runnerStartFlowsFormatter',
  'flowinglyConstants',
  'flowModelApiService',
  'governanceApiService'
];

export interface IFormattedCategoryWithCount<T> {
  name: string;
  id: Guid | number;
  flowModels: T[];
  subCategories: IFormattedCategoryWithCount<T>[];
  count: number;
}

function flowListManager(
  runnerStartFlowsFormatter: RunnerStartFlowsFormatter,
  flowinglyConstants: SharedAngular.FlowinglyConstants,
  flowModelApiService: SharedAngular.FlowModelApiService,
  governanceApiService: GovernanceApiService
) {
  const _defaultProcessMapCategoryId = 'all-categories';
  const _defaultExpandedCategoriesList: IExpandedCategoryInfo = {
    ids: [],
    idField: ''
  };
  const _defaultActiveFlowCategoryId = 'all-categories';

  let _selectedProcessMapCategoryId = _defaultProcessMapCategoryId;
  let _expandedCategoriesList = _defaultExpandedCategoriesList;
  let _selectedActiveFlowCategoryId = _defaultActiveFlowCategoryId;

  const service = {
    reportFlowModels: [] as IFormattedCategory<IReportFlowModelDetail>[],
    groupedFlowsImin: [] as IGroupedFlowsForUserPascalCase[], // Flows Im In grouped by the selected group and status
    iminFlowFilters: [] as IFlowFilter[],
    statusOptions: [
      { name: 'In Progress', id: 'InProgress' },
      { name: 'All', id: 'All' },
      { name: 'Completed', id: 'Completed' },
      { name: 'Rejected', id: 'Rejected' },
      { name: 'Cancelled', id: 'Cancelled' }
    ],
    flowImInCategories:
      [] as IFormattedCategoryWithCount<IGroupedFlowsForUserPascalCase>[],

    // -------------Methods----------------

    clearReportModelList: clearReportModelList,
    getActiveFlowCategoryId: getActiveFlowCategoryId,
    getExpandedCategoriesList: getExpandedCategoriesList,
    setProcessMapCategoryId: setProcessMapCategoryId,
    getProcessMapCategoryId: getProcessMapCategoryId,
    setActiveFlowCategoryId: setActiveFlowCategoryId,
    setExpandedCategoriesList: setExpandedCategoriesList,

    getCategorizedWorkflows: getCategorizedWorkflows,
    getCategorizedProcessMaps,

    refreshReportFlowModels: refreshReportFlowModels,

    updateFlowNames: updateFlowNames //user has changed flow name in modeler, update here
  };

  return service;

  //////////// Public API Methods

  function clearReportModelList() {
    service.reportFlowModels = [];
  }

  function getProcessMapCategoryId() {
    return _selectedProcessMapCategoryId;
  }

  function getExpandedCategoriesList() {
    return _expandedCategoriesList;
  }

  function getActiveFlowCategoryId() {
    return _selectedActiveFlowCategoryId;
  }

  function setActiveFlowCategoryId(id: string) {
    _selectedActiveFlowCategoryId = id;
  }

  function setExpandedCategoriesList(
    expandedCategoryInfo: IExpandedCategoryInfo
  ) {
    _expandedCategoriesList = expandedCategoryInfo;
  }

  function setProcessMapCategoryId(id: string) {
    _selectedProcessMapCategoryId = id;
  }

  function updateFlowNames(data) {
    service.reportFlowModels = updateFlowModelNameInList(
      data,
      service.reportFlowModels,
      true,
      'flowModels'
    );
  }

  function updateFlowModelNameInList(msgDetails, source, camel, property?) {
    if (property === undefined) {
      property = camel ? 'flows' : 'Flows';
    }
    //we still have a mix of Pascal and Camel, rather than try and fix now and risk breaking other things
    //simply switch on a flag
    let changesMade = false;
    if (camel) {
      source.forEach((g) => {
        g[property].forEach((f) => {
          if (f.id === msgDetails.id) {
            changesMade = changesMade || f.name !== msgDetails.name;
            f.name = msgDetails.name;
            f.flowModelName = msgDetails.name;
          }
        });
      });
    } else {
      source.forEach((g) => {
        g[property].forEach((f) => {
          if (f.FlowModelId === msgDetails.id) {
            changesMade = changesMade || f.Name !== msgDetails.name;
            f.Name = msgDetails.name;
            g.GroupName = msgDetails.name;
          }
        });
      });
    }
    return changesMade ? angular.copy(source) : source;
  }

  /* End of GetFlows API calls */

  function getCategorizedWorkflows(noSpinner = false) {
    return flowModelApiService
      .getPublishedFlowModels(
        noSpinner,
        flowinglyConstants.flowModelPublishType.WORKFLOW
      )
      .then((flowModels) =>
        runnerStartFlowsFormatter.groupObjectsByCategory(flowModels)
      );
  }

  function getCategorizedProcessMaps(noSpinner: boolean) {
    return flowModelApiService
      .getPublishedFlowModels(
        noSpinner,
        flowinglyConstants.flowModelPublishType.PROCESS_MAP
      )
      .then((flowModels) =>
        runnerStartFlowsFormatter.groupObjectsByCategory(flowModels)
      );
  }

  function refreshReportFlowModels(noSpinner: boolean) {
    return flowModelApiService
      .getReportFlowModels(noSpinner)
      .then(async (reportFlowModels) => {
        await formatReportFlowModels(reportFlowModels);
        return service.reportFlowModels;
      });
  }

  //////////// Private Methods

  // The following format methods simply call other methods to format the API responses.
  // No formatting should be completed in this service itself
  // They are however responsible for updating the list contents

  async function formatReportFlowModels(
    reportFlowModels: IReportFlowModelDetail[]
  ) {
    const activeFlows =
      runnerStartFlowsFormatter.groupObjectsByCategory(reportFlowModels);
    angular.copy(activeFlows, service.reportFlowModels);
  }
}

interface IFlowFilter {
  name: string;
  id: Guid | string; // String because the default id is "1", should be changed to an actual guid
  flowCategory?: string;
  flowCategoryId?: Guid;
}

export type FlowListManagerType = ReturnType<typeof flowListManager>;
